import { UnitType } from "dayjs";

import {
  MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD,
  SECONDS_IN_MINUTE,
  STORE_ROUTE_CACHE_DAYS_DEFAULT,
  ZERO_TIME_HHmm,
} from "../../constants";
import { TimeZoneEnum } from "../../enums";
import {
  ILocationInfo,
  ILocationInfoValid,
  IRideRoute,
  IStationRouteComputedData,
  IStationRouteDto,
  IStationRouteStationInfo,
  TTimeHHmm,
} from "../../models";
import {
  addSecondsToHHmmTimeString,
  isValidTimeString,
  timeDifferenceSameDate,
} from "../date.helpers";
import { durationInMinutesCeil } from "../formula.helpers";
import { Log } from "../log.helpers";
import { safeParseNumericStringOrUndefined } from "../numbers.helpers";
import { getStationRouteRealStations } from "./station-route-waypoint-ui.helpers";
import { IStationRouteStationDurationDetails } from "./station-route.models";

export const getStationRoute = (
  stationRoute?: Partial<IStationRouteDto>,
): ILocationInfo[] => {
  return (
    stationRoute?.computedData?.stationInfos?.map(stationInfo => {
      const location: ILocationInfo = {
        ...stationInfo.locationInfo,
        addressName: stationInfo.name,
        fullAddress: stationInfo.name,
        waypointCategory: stationInfo.waypointCategory,
        stationId: stationInfo.waypointCategory ? undefined : stationInfo.id,
      };
      return location;
    }) || []
  );
};

export const getStationRouteStationById = (
  stationRouteDto: IStationRouteDto,
  stationId: number | undefined,
): IStationRouteStationInfo | undefined => {
  if (!stationId || !stationRouteDto?.computedData?.stationInfos) {
    return undefined;
  }

  return stationRouteDto.computedData.stationInfos.find(
    x => x.id === stationId,
  );
};

/** Last station clock from ceiled `result.totalDuration`; stored HH:mm when total is missing. */
export const getStationRouteDisplayStationInfos = ({
  stationRouteDto,
  timezone,
}: {
  stationRouteDto: Partial<IStationRouteDto> | undefined;
  timezone: TimeZoneEnum;
}): IStationRouteStationInfo[] => {
  const stations = stationRouteDto?.computedData?.stationInfos || [];
  if (stations.length < MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD) {
    return stations;
  }

  const ceiledMinutes = durationInMinutesCeil(
    stationRouteDto?.result?.totalDuration,
  );
  if (!ceiledMinutes) {
    return stations;
  }

  const firstTimeArrival = stations[0]?.timeArrival;
  if (!firstTimeArrival || !isValidTimeString(firstTimeArrival)) {
    return stations;
  }

  const desiredLastArrival = addSecondsToHHmmTimeString(
    firstTimeArrival,
    ceiledMinutes * SECONDS_IN_MINUTE,
    timezone,
  );
  if (!desiredLastArrival || !isValidTimeString(desiredLastArrival)) {
    return stations;
  }

  const lastIndex = stations.length - 1;
  if (stations[lastIndex]?.timeArrival === desiredLastArrival) {
    return stations;
  }

  return stations.map((station, index) =>
    index === lastIndex
      ? { ...station, timeArrival: desiredLastArrival }
      : station,
  );
};

export const getStationRouteDisplayStationById = ({
  stationRouteDto,
  stationId,
  timezone,
}: {
  stationRouteDto: IStationRouteDto;
  stationId: number | undefined;
  timezone: TimeZoneEnum;
}): IStationRouteStationInfo | undefined => {
  if (!stationId) {
    return undefined;
  }

  return getStationRouteDisplayStationInfos({
    stationRouteDto,
    timezone,
  }).find(station => station.id === stationId);
};

export const getStationRouteInfo = (
  stationRouteDto: Partial<IStationRouteDto>,
): IRideRoute => {
  const locations = getStationRoute(stationRouteDto);
  const stationRoute: IRideRoute = {
    locations,
    routePolyline: stationRouteDto?.result?.routePolyline,
    stationRouteId: stationRouteDto?.id,
  };
  return stationRoute;
};

export const getStationRouteRoutesInfo = (
  stationRouteDto?: Partial<IStationRouteDto>,
): IRideRoute[] => {
  return stationRouteDto?.result?.routePolyline
    ? [getStationRouteInfo(stationRouteDto)]
    : [];
};

export const getStationRouteDurationsLegs = (
  stations: IStationRouteStationInfo[],
  timezone: TimeZoneEnum,
): number[] => {
  if (!stations?.length) {
    return [];
  }

  const durationsInSeconds: number[] = [];

  for (let i = 0; i < stations.length - 1; i++) {
    const durationSeconds = getStationRouteStationDuration(
      stations,
      i,
      timezone,
      "second",
    );
    durationsInSeconds.push(durationSeconds || 0);
  }

  return durationsInSeconds;
};

export const getStationRouteStationDuration = (
  stations: IStationRouteStationInfo[] | undefined,
  index: number,
  timezone: TimeZoneEnum,
  unit: UnitType = "second",
): number | undefined => {
  if (!stations?.length || index >= stations.length) {
    return undefined;
  }

  const timeA = stations[index]?.timeArrival;
  const timeB = stations[index + 1]?.timeArrival;

  if (
    !timeA ||
    !timeB ||
    !isValidTimeString(timeA) ||
    !isValidTimeString(timeB)
  ) {
    return undefined;
  }

  return timeDifferenceSameDate(timeA, timeB, unit, timezone);
};

const resolveStationRouteLegSeconds = ({
  stationRouteDto,
  index,
  timezone,
}: {
  stationRouteDto: Partial<IStationRouteDto> | undefined;
  index: number;
  timezone: TimeZoneEnum;
}): number | undefined => {
  const hhmmSeconds = getStationRouteStationDuration(
    stationRouteDto?.computedData?.stationInfos,
    index,
    timezone,
    "second",
  );
  const providerSeconds = stationRouteDto?.result?.durations?.[index];
  const hasProviderSeconds =
    typeof providerSeconds === "number" && Number.isFinite(providerSeconds);

  if (
    hasProviderSeconds &&
    (hhmmSeconds === undefined ||
      Math.abs(providerSeconds - hhmmSeconds) < SECONDS_IN_MINUTE)
  ) {
    return Math.max(providerSeconds, 0);
  }

  return hhmmSeconds === undefined ? undefined : Math.max(hhmmSeconds, 0);
};

export const getStationRouteStationDisplayDurationMin = ({
  stationRouteDto,
  index,
  timezone,
}: {
  stationRouteDto: Partial<IStationRouteDto> | undefined;
  index: number;
  timezone: TimeZoneEnum;
}): number | undefined => {
  const stations = stationRouteDto?.computedData?.stationInfos;
  const legsCount = (stations?.length || 0) - 1;

  if (!stations?.length || index < 0 || index >= legsCount) {
    return undefined;
  }

  const durationSeconds = resolveStationRouteLegSeconds({
    stationRouteDto,
    index,
    timezone,
  });
  if (durationSeconds === undefined) {
    return undefined;
  }

  return durationInMinutesCeil(durationSeconds) ?? undefined;
};

export const getStationRouteStationDurationDetails = ({
  stationRouteDto,
  index,
  timezone,
}: {
  stationRouteDto: Partial<IStationRouteDto> | undefined;
  index: number;
  timezone: TimeZoneEnum;
}): IStationRouteStationDurationDetails => {
  const stations = getStationRouteDisplayStationInfos({
    stationRouteDto,
    timezone,
  });

  if (!stations?.length || index >= stations.length) {
    return {};
  }

  const previousStationTime = stations[index]?.timeArrival;
  const nextStationTime = stations[index + 1]?.timeArrival;

  if (
    !previousStationTime ||
    !nextStationTime ||
    !isValidTimeString(previousStationTime) ||
    !isValidTimeString(nextStationTime)
  ) {
    const partialDetails: IStationRouteStationDurationDetails = {
      previousStationTime,
      nextStationTime,
    };
    return partialDetails;
  }

  const durationSeconds =
    resolveStationRouteLegSeconds({ stationRouteDto, index, timezone }) ?? 0;
  const durationMinutes = Math.floor(durationSeconds / SECONDS_IN_MINUTE);

  const details: IStationRouteStationDurationDetails = {
    previousStationTime,
    nextStationTime,
    durationSeconds,
    durationMinutes,
  };
  return details;
};

export const updateStationRouteComputedDataStationsTime = (
  stationRouteDto: IStationRouteDto,
  editStationId: number,
  timeStringHHmm: TTimeHHmm,
): IStationRouteComputedData => {
  const stations = [...(stationRouteDto?.computedData?.stationInfos || [])];
  const stationChangeFromIndex = stations.findIndex(
    x => x.id === editStationId,
  );
  const stationsUpdated: IStationRouteStationInfo[] = stations.map(
    (station, index) => {
      if (index !== stationChangeFromIndex) {
        return {
          ...station,
          timeArrival: station.timeArrival || ZERO_TIME_HHmm,
        };
      }

      return { ...station, timeArrival: timeStringHHmm || ZERO_TIME_HHmm };
    },
  );

  const computedDataToUpdate: IStationRouteComputedData = {
    ...(stationRouteDto.computedData || {}),
    stationInfos: stationsUpdated,
  };
  Log.debug(
    `Ride metadata after time update from index ${stationChangeFromIndex} by ${timeStringHHmm} min.:`,
    computedDataToUpdate,
  );
  return computedDataToUpdate;
};

/** Header minutes from provider `result.totalDuration` (ceiled). Falls back to the HH:mm first–last span. */
export const getStationRouteDurationMin = (
  stationRouteDto: Partial<IStationRouteDto> | undefined,
  timezone: TimeZoneEnum,
): number => {
  const fromTotalDuration = durationInMinutesCeil(
    stationRouteDto?.result?.totalDuration,
  );
  if (fromTotalDuration) {
    return fromTotalDuration;
  }

  if (!stationRouteDto) {
    return 0;
  }

  const stationRouteStations = getStationRouteRealStations(
    stationRouteDto?.computedData?.stationInfos,
  );

  if (
    !stationRouteStations?.length ||
    stationRouteStations?.length < MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD
  ) {
    return 0;
  }

  const firstStation = stationRouteStations.at(0);
  const lastStation = stationRouteStations.at(-1);

  if (
    !firstStation?.timeArrival ||
    !lastStation?.timeArrival ||
    !isValidTimeString(firstStation.timeArrival) ||
    !isValidTimeString(lastStation.timeArrival)
  ) {
    return 0;
  }

  const time = timeDifferenceSameDate(
    firstStation.timeArrival,
    lastStation.timeArrival,
    "minute",
    timezone,
  );

  return time ?? 0;
};

export const getIsValidStationLocation = (
  stationLocation?: ILocationInfo | null,
): stationLocation is ILocationInfoValid =>
  Boolean(stationLocation?.latitude && stationLocation?.longitude);

/**
 * Current effective route-cache retention from system params.
 * Matches BE freshness / clearStaleRoutes (not per-row metadata.cacheDurationDays).
 * Unset / empty / invalid / negative → STORE_ROUTE_CACHE_DAYS_DEFAULT.
 */
export const resolveCurrentStoreRouteCacheDays = (
  storeRouteCacheDays?: number | string,
): number => {
  const fromSystemParams =
    safeParseNumericStringOrUndefined(storeRouteCacheDays);
  if (fromSystemParams === undefined || fromSystemParams < 0) {
    return STORE_ROUTE_CACHE_DAYS_DEFAULT;
  }
  return fromSystemParams;
};

/**
 * Retention days stored on the row when it was written (`metadata.cacheDurationDays`).
 * Undefined when missing / non-finite (older rows may omit it).
 */
export const getSavedStationRouteCacheDurationDays = (
  cacheDurationDays?: number,
): number | undefined => {
  if (
    typeof cacheDurationDays === "number" &&
    Number.isFinite(cacheDurationDays) &&
    cacheDurationDays >= 0
  ) {
    return cacheDurationDays;
  }
  return undefined;
};

/**
 * Display TTL for station-route card: always current system `storeRouteCacheDays`
 * (aligned with BE lookup / purge). Saved metadata value is separate — see
 * {@link getSavedStationRouteCacheDurationDays}.
 *
 * @deprecated Prefer {@link resolveCurrentStoreRouteCacheDays}; `cacheDurationDays` is ignored.
 */
export const resolveStationRouteCacheDurationDays = ({
  storeRouteCacheDays,
}: {
  cacheDurationDays?: number;
  storeRouteCacheDays?: number | string;
}): number => resolveCurrentStoreRouteCacheDays(storeRouteCacheDays);

export const formatMatchingMlPathHeaderValue = ({
  updateId,
  count,
  newLabel,
}: {
  updateId: number | null;
  count: number;
  newLabel: string;
}): string => {
  if (!updateId) {
    return newLabel;
  }

  if (count > 1) {
    return `${updateId} (${count})`;
  }

  return String(updateId);
};
